import {
  IInferenceModel,
  INFERENCE_MODEL_KEY,
  NotificationType,
  useGenerationPresets,
  useModelsByTier,
  useTranslation,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Col } from "@/components";
import Trans from "@/components/Trans/Trans";
import { FEATURE_FLAGS, SPICYCHAT_USER_PERMISSIONS } from "@/constants";
import { useFeatureFlag, useInferenceSettings } from "@/hooks";
import useToast from "@/hooks/useToast";
import ModelSelectionHeader from "@/pages/Chat/components/ModelSelectionDropdown/ModelSelectionHeader";
import ModelSelectionItem from "@/pages/Chat/components/ModelSelectionDropdown/ModelSelectionItem";
import { ChatPageModal } from "@/pages/Chat/components/types";
import useAuth from "@/services/useAuth";
import { typedLocalStorageService } from "@/utils/localStorageRe";

interface IMoreOption {
  name: string;
  description: string;
  action: () => void;
}

const renderMoreOption = (option: IMoreOption) => (
  <ModelSelectionItem key={option.name} model={{ name: option.name, description: option.description } as IInferenceModel} action={option.action} />
);

const ModelSelectionOverview = ({
  toggleView,
  togglePresetView,
  closeDropdown,
  handleModalVisibility,
  setIsModelSelectOpen,
}: {
  toggleView: () => void;
  togglePresetView: () => void;
  closeDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  handleModalVisibility: (modal: ChatPageModal, isOpen: boolean) => void;
  setIsModelSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const { getModelsByTier } = useModelsByTier();
  const { inferenceModel: selectedModelId, updateInferenceModel } = useInferenceSettings();
  const { availableModels, premiumModels } = getModelsByTier(selectedModelId);
  const { availablePresets } = useGenerationPresets();
  const { permissions } = useAuth();
  const hasGenerationSettings = permissions?.includes(SPICYCHAT_USER_PERMISSIONS.generation_settings);
  const { hasFeatureFlag: hasPresetsFeature } = useFeatureFlag(FEATURE_FLAGS.GENERATION_PRESETS);
  const isGenerationSettingsAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ChatGenerationSettings);

  const exploreAllModelsOption: IMoreOption = {
    name: t("chat:page.header.modelSelection.dropdown.exploreAllModels.title"),
    description: t("chat:page.header.modelSelection.dropdown.exploreAllModels.description"),
    action: () => {
      setIsModelSelectOpen(true);
      closeDropdown(false);
    },
  };

  const generationSettingsOption: IMoreOption = {
    name: t("chat:page.header.modelSelection.dropdown.generationSettings.title"),
    description: t("chat:page.header.modelSelection.dropdown.generationSettings.description"),
    action: () => {
      handleModalVisibility(ChatPageModal.SHOW_MODEL_SETTINGS, true);
      closeDropdown(false);
    },
  };

  const moreOptions = isGenerationSettingsAllowed ? [exploreAllModelsOption, generationSettingsOption] : [exploreAllModelsOption];
  const navigate = useNavigate();
  const navigateToSubscribe = useCallback(() => {
    navigate("/subscribe");
  }, [navigate]);
  const handleModelSelection = useCallback(
    ({ id, name }: { id: string; name: string }) => {
      updateInferenceModel(id);
      typedLocalStorageService.setItem(INFERENCE_MODEL_KEY, id);
      displayToast({
        message: t("chat:page.header.modelSelection.dropdown.switchModel.toast", { modelName: name }),
        type: NotificationType.success,
      });
      closeDropdown(false);
    },
    [closeDropdown, displayToast, t, updateInferenceModel],
  );
  return (
    <Col className="max-w-[330px] max-h-[696px] overflow-y-auto custom-scroll py-sm">
      <ModelSelectionHeader canGoBack={false} clickHandler={toggleView} />
      {isGenerationSettingsAllowed && hasPresetsFeature && hasGenerationSettings && availablePresets.length > 0 ? (
        <>
          <ModelSelectionItem
            model={
              {
                name: t("chat:modal.generationSettings.presets.titleWithCount", { count: availablePresets.length }),
                description: t("chat:page.header.modelSelection.dropdown.presets.description"),
              } as IInferenceModel
            }
            action={togglePresetView}
          />
          <div className="h-[1px] bg-gray-5 mx-[18px] dark:border-gray-6" />
        </>
      ) : null}
      {availableModels.map((model: IInferenceModel) => {
        return <ModelSelectionItem key={model.id} model={model} activeModel={selectedModelId} clickHandler={handleModelSelection} />;
      })}
      {premiumModels.length > 0 && (
        <Col>
          <div className="bg-gray-2 dark:bg-gray-4 py-1 px-[18px]">
            <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM} className="text-gray-10">
              <Trans i18nKey="chat:page.header.modelSelection.dropdown.premiumModels.title" />
            </Text>
          </div>
          {premiumModels.map((model: IInferenceModel) => {
            return <ModelSelectionItem key={model.id} clickHandler={navigateToSubscribe} model={model} isLocked />;
          })}
        </Col>
      )}
      <div className="h-[1px] bg-gray-5 mx-[18px] dark:border-gray-6" />
      {moreOptions.map(renderMoreOption)}
    </Col>
  );
};

export default ModelSelectionOverview;
